// src/data/contactDetails.js
//
// Record 610 (ruling 259/262). EVERYONE'S MAILING ADDRESS IS KEPT ON THE PLATFORM; A MISSING ONE IS ASKED FOR, WITH
// GRACE. Ruling 259 (data\YCP_RULINGS_2026-09-29.md, "## 259."): "A missing address is asked for, at the moment it is
// needed, from the person it belongs to: an inline prompt that saves to the profile and continues; a dismissible
// reminder at sign-in; a Contact section in the profile; the office address entered once by the seat holder."
// The inline prompt was built 2026-09-29 (ClaimFormActions.js). This module is the rest, pure:
//
//   THE PROFILE FIELDS ARE THE ONES ALREADY THERE. users/{uid}.street / city / state / zip / phone - the same keys the
//   inline prompt writes and penaltyClaim.js claimFormModel() reads for an officer's address, all already in
//   firestore.rules memberWritableOnly(). No parallel field is invented.
//
//   THE E-MAIL IS SHOWN, NOT EDITED HERE. users/{uid}.email pre-fills claimantEmail on the full form (NewClaim.js), and
//   claimantEmail is how the platform knows the filer IS the claimant (memberProfile.js filerIsClaimant; firestore.rules
//   isClaimOfSelf). A profile e-mail different from the sign-in address would make the member's next claim read as
//   filed for somebody else, and she could then not record her own choices on it. So the Contact section shows the
//   sign-in address and says how it is changed; the phone and the address are hers to edit.
//
//   THE OFFICE ADDRESS IS THE SEAT'S. A General Chairperson's office address is his committee letterhead's address
//   lines (committees/{c}.letterhead.addressLines, ruling 229 - entered once by the GC, kept on the seat). A Local
//   Chairperson's is entered once on his seat's directory (directories/{c}/locals/{scopeKey}.officeAddress, Record 610).
//   officeAddressFor() picks it for the form; without one, the officer's own profile address prints, as before.
//
// Pure: no React, no Firestore, no imports.

export const ADDRESS_KEYS = ['street', 'city', 'state', 'zip'];
// 'name' (Bob 2026-10-12, "3. a"): the name that prints under his signature and on his claims - his to correct, in his own
// profile (users/{uid}.name, already in memberWritableOnly). Each copy served keeps the name it was served with
// (presentmentSigner), so a later correction never rewrites a claim already on the carrier's desk.
export const CONTACT_KEYS = ['name', 'street', 'city', 'state', 'zip', 'phone'];   // what the Contact section writes
const s = (v) => (v == null ? '' : String(v).replace(/[\r\n]+/g, ' ').trim());

export function hasMailingAddress(p) {
  return !!p && ADDRESS_KEYS.every((k) => !!s(p[k]));
}

// The two printed lines, in the exact form claimFormModel() and ClaimFormActions build them.
export function mailingAddressLines(p) {
  if (!p) return [];
  return [s(p.street), [s(p.city), [s(p.state), s(p.zip)].filter(Boolean).join(' ')].filter(Boolean).join(', ')].filter(Boolean);
}

// The Contact section's write. Blank is allowed (a person may clear his phone), but an address is all four parts or
// none: half an address is worse than none, because it prints. Returns { ok, errors, patch } - patch holds only
// CONTACT_KEYS, every one of them in firestore.rules memberWritableOnly(); the caller passes it through profilePatch().
export function contactPatchFor(input) {
  const i = input || {};
  const v = {};
  CONTACT_KEYS.forEach((k) => { v[k] = s(i[k]).slice(0, 120); });
  if (v.state.length === 2) v.state = v.state.toUpperCase();   // "ny" -> "NY"; a state written out is left as written
  const errors = [];
  const some = ADDRESS_KEYS.some((k) => v[k]);
  const all = ADDRESS_KEYS.every((k) => v[k]);
  if (some && !all) errors.push('Street, city, state and ZIP are all needed for a mailing address.');
  if (v.zip && !/^\d{5}(-?\d{4})?$/.test(v.zip)) errors.push('The ZIP code is five digits (or ZIP+4).');
  if (v.phone && v.phone.replace(/[^0-9]/g, '').length < 10) errors.push('The phone number needs its area code.');
  if (errors.length) return { ok: false, errors, patch: null };
  return { ok: true, errors: [], patch: v };
}

// The sign-in reminder: due when the profile has no complete mailing address and the person has not set it aside
// this session. A profile that could not be READ is not "missing" - the reminder stays quiet rather than nag on a
// failed read (profile === undefined means not read).
export function reminderDue(profile, dismissed) {
  if (dismissed) return false;
  if (profile === undefined) return false;
  return !hasMailingAddress(profile || {});
}
export function reminderKey(uid) { return 'ycp.r259.addressReminder.' + s(uid); }

// The office address that prints under an officer's signature on a claim he submits.
//   by       'general_chair' | 'local_chair' | 'claimant'
//   sources  { letterhead: committees/{c}.letterhead, local: directories/{c}/locals/{scopeKey} }
// Returns the lines, or [] when the seat holds none (then the officer's own profile address prints, as before).
export function officeAddressFor(by, sources) {
  const x = sources || {};
  const clean = (arr) => (Array.isArray(arr) ? arr.map(s).filter(Boolean) : []);
  let out = [];
  if (by === 'general_chair') out = clean(x.letterhead && x.letterhead.addressLines);
  else if (by === 'local_chair') out = clean(x.local && x.local.officeAddress);
  return out.length >= 2 ? out : [];
}
